import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, catchError, Observable, of, Subscription, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { ContentUtil } from '../../../../utils/content/content.util';
import { GranteeAlmostThereContent } from '../../../../utils/content/aem-content-types';

import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { selectGranteeAccessType } from '../../../../store/selectors/grantee.selector';
import { APPLICATION_ROUTES, TOKEN_PARAM } from '../../../../constants/application-routes';
import { BreakpointObserverService } from '../../../../services/breakpoint-observer/breakpoint-observer.service';

import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { configData } from './almost-there-adobe';
import { LoggerService } from '../../../../services/logger/logger.service';
import { environment } from 'src/environments/environment';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { ButtonModule } from '@vg-constellation/angular-20/button';

@AdobeTagging(configData)
@Component({
    selector: 'zci-almost-there',
    templateUrl: './almost-there.component.html',
    styleUrls: ['./almost-there.component.scss'],
    imports: [
        AsyncPipe,
        NgClass,
        NgIf,
        ButtonModule,
    ],
})
export class AlmostThereComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly breakpointObserverService = inject(BreakpointObserverService);
    private readonly logger = inject(LoggerService);

    content: GranteeAlmostThereContent = ContentUtil.granteeAlmostThereContent;
    heading: string = this.content.heading;
    setUpOnlineAccessListItem: string = this.content.setUpOnlineAccessListItem;
    logInAndEnterPinListItem: string = this.content.logInAndEnterPinListItem;
    acceptAccessRequestListItem: string = this.content.acceptAccessRequestListItem;

    stepOneScreenReaderLabel: string = `Step 1: Complete. ${this.setUpOnlineAccessListItem}`;
    stepTwoLimitedAccessScreenReaderLabel: string = `Step 2: Current step: ${this.logInAndEnterPinListItem}`;
    stepTwoInfoOnlyScreenReaderLabel: string = `Step 2: Current and final step: ${this.logInAndEnterPinListItem}`;
    stepThreeScreenReaderLabel: string = `Step 3: Next and final step: ${this.acceptAccessRequestListItem}`;

    isSmallView$: Observable<boolean> = this.breakpointObserverService.isSmallView$;
    isMobileView$: Observable<boolean> = this.breakpointObserverService.isMobileView$;
    isTabletLargeView$: Observable<boolean> = this.breakpointObserverService.isTabletLargeView$;
    isTabletXLargeView$: Observable<boolean> = this.breakpointObserverService.isTabletXLargeView$;
    isTabletView$: Observable<boolean> = this.breakpointObserverService.isTabletView$;

    isInformationOnly$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    accessType: AccessTypeSelection;
    granteeAccessType$: Observable<AccessTypeSelection> =
        this.store.select(selectGranteeAccessType);

    readonly accessTypeSelection = AccessTypeSelection;
    readonly subs: Subscription = new Subscription();

    private tokenValue: string;

    ngOnInit(): void {
        this.subs.add(
            this.granteeAccessType$.subscribe((accessType) => {
                this.accessType = accessType;
                switch (accessType) {
                    case this.accessTypeSelection.INFORMATION_ONLY:
                        this.isInformationOnly$.next(true);
                        this.adobeInfoOnlyPageLoad();
                        break;
                    case this.accessTypeSelection.LIMITED_ACCESS:
                        this.isInformationOnly$.next(false);
                        this.adobeLimitedPageLoad();
                        break;
                }
            }),
        );

        this.subs.add(
            this.route.paramMap
                .pipe(
                    switchMap((params: ParamMap) => {
                        return (this.tokenValue = params.get(TOKEN_PARAM));
                    }),
                    catchError((error) => {
                        this.logger.error(
                            'Error in AlmostThereComponent while getting token',
                            error,
                        );
                        return of(null);
                    }),
                )
                .subscribe(),
        );
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }


    loginButtonClick(): void {
        this.handleLoginAdobe();
        const acceptAccessPath = `${APPLICATION_ROUTES.ACCEPT_ACCESS.replace(':token', this.tokenValue)}`
        const targetUrl = encodeURIComponent(`${environment.APP_URL}/${acceptAccessPath}`)
        window.location.href = `${environment.LOGON_REDIRECT_URL}${targetUrl}`;
    }

    setClassForInfoOnlyStepTwoSpacing(
        isSmallView: boolean,
        isMobileView: boolean,
        isTabletLargeView: boolean,
        isTabletXLargeView: boolean,
        isTabletView: boolean,
    ): string {
        const emptyString: string = '';
        const spaceStack1_5x: string = 'c11n-space-stack-1_5x c11n-space--force';
        const spaceStack2x: string = 'c11n-space-stack-2x c11n-space--force';
        const spaceStack3x: string = 'c11n-space-stack-3x c11n-space--force';

        if (
            isSmallView &&
            isMobileView &&
            isTabletLargeView &&
            isTabletXLargeView &&
            isTabletView
        ) {
            return emptyString;
        }

        if (isMobileView && isTabletLargeView && isTabletXLargeView && isTabletView) {
            return spaceStack3x;
        }
        if (isTabletLargeView && isTabletXLargeView && isTabletView) {
            return spaceStack3x;
        }
        if (isTabletXLargeView && isTabletView) {
            return spaceStack1_5x;
        }
        return spaceStack2x;
    }

    private adobeInfoOnlyPageLoad(): void { }

    private adobeLimitedPageLoad(): void { }

    private adobeLoginInfoOnly(): void { }

    private adobeLoginLimited(): void { }

    private handleLoginAdobe(): void {
        this.subs.add(
            this.granteeAccessType$.subscribe((accessType) => {
                this.accessType = accessType;
                switch (accessType) {
                    case this.accessTypeSelection.INFORMATION_ONLY:
                        this.adobeLoginInfoOnly();
                        break;
                    case this.accessTypeSelection.LIMITED_ACCESS:
                        this.adobeLoginLimited();
                        break;
                }
            }),
        );
    }
}
